import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

/**
 * WaitlistBanner - Displays waitlist status and position for player portal
 * Shows when a player is on the waitlist with their current position
 * Animates when position changes
 */
const WaitlistBanner = ({ waitlistData, className }) => {
  const [previousPosition, setPreviousPosition] = useState(null);
  const [positionChanged, setPositionChanged] = useState(false);

  useEffect(() => {
    if (!waitlistData?.position) return;

    if (previousPosition !== null && previousPosition !== waitlistData.position) {
      setPositionChanged(true);
      setTimeout(() => setPositionChanged(false), 3000); // Clear highlight after 3 seconds
    }

    setPreviousPosition(waitlistData.position);
  }, [waitlistData?.position]);

  if (!waitlistData || !waitlistData.position) {
    return null;
  }

  const { position, signupTimestamp, addedToWaitlistAt } = waitlistData;
  const signupDate = new Date(signupTimestamp || addedToWaitlistAt);
  const formattedDate = signupDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const positionMovedUp = previousPosition !== null && position < previousPosition;
  const positionMovedDown = previousPosition !== null && position > previousPosition;

  return (
    <div className={cn('bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 transition-all', className)}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-yellow-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">
            You are on the waitlist
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <div className="flex items-center gap-2">
              <p className={cn(
                'font-semibold text-lg transition-all',
                positionChanged && 'animate-pulse'
              )}>
                Position #{position}
              </p>
              {positionChanged && positionMovedUp && (
                <span className="inline-flex items-center text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
                  </svg>
                  Moved up!
                </span>
              )}
              {positionChanged && positionMovedDown && (
                <span className="inline-flex items-center text-xs font-medium text-orange-700 bg-orange-100 px-2 py-1 rounded-full">
                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
                  </svg>
                  Position changed
                </span>
              )}
            </div>
            <p className="text-xs mt-1">
              Added to waitlist on {formattedDate}
            </p>
            <p className="text-xs mt-2 text-yellow-600">
              {positionChanged
                ? "Your position has changed. This page updates automatically every 30 seconds."
                : "You'll be notified if a spot opens up. Your position may change as others join or leave the waitlist."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistBanner;
